import { watch } from 'vue';

import type { IdoStore } from '@/store/interfaces/IdoStore';

type Handler = (payload: any, type: string, state: any) => any;

// The legacy ApplicationObserver store contract, kept alive for
// trackIdoEvents (and its ApplicationObserverMock-driven spec).
export interface LegacyIdoObserver {
    onValue(key: string, handler: (value: any, oldValue: any) => any): void;
    onChange(getter: (state: any) => any, handler: (value: any, oldValue: any) => any): void;
    onAction(type: string, handler: Handler): void;
    onActionFinish(type: string, handler: Handler): void;
    onMutation(type: string, handler: (payload: any, type: string) => any): void;
    onRouteChange(handler: (to: any, from: any, state: any) => any): void;
    onRoute(name: string, handler: (route: any, from: any) => any): void;
    removeListeners(): void;
}

// PRODUCT-26110 Pinia migration: adapts the Pinia store to the legacy
// ApplicationObserver contract trackIdoEvents consumes, so its 300 lines of
// event wiring (and spec, driven by ApplicationObserverMock) stay unchanged.
// - onValue/onChange -> vue watch() on store state
// - onAction -> $onAction before-callback (Vuex subscribeAction timing)
// - onActionFinish -> $onAction after() (Vuex subscribeAction.after timing)
// - onMutation -> $onAction after() — legacy mutations are now same-named
//   sync actions, and Vuex fired subscribe() only after the mutation ran
// - onRouteChange -> the app observer's router hook, with the store as state
export function createIdoStoreObserver(
    store: IdoStore,
    routeObserver: { onRouteChange(handler: (to: any, from: any) => any): void },
): LegacyIdoObserver {
    const before: Record<string, Handler[]> = {};
    const after: Record<string, Handler[]> = {};

    store.$onAction(({ name, args, after: onAfter }) => {
        (before[name] || []).forEach((handler) => handler(args[0], name, store));
        if (after[name]) {
            onAfter(() => after[name].forEach((handler) => handler(args[0], name, store)));
        }
    });

    const observer = {
        onValue(key: string, handler: (value: any, oldValue: any) => any) {
            watch(() => (store as any)[key], handler);
        },
        onChange(getter: (state: any) => any, handler: (value: any, oldValue: any) => any) {
            watch(() => getter(store), handler);
        },
        onAction(type: string, handler: Handler) {
            (before[type] = before[type] || []).push(handler);
        },
        onActionFinish(type: string, handler: Handler) {
            (after[type] = after[type] || []).push(handler);
        },
        onMutation(type: string, handler: (payload: any, type: string) => any) {
            (after[type] = after[type] || []).push((payload, actionType) => handler(payload, actionType));
        },
        onRouteChange(handler: (to: any, from: any, state: any) => any) {
            routeObserver.onRouteChange((to, from) => handler(to, from, store));
        },
        onRoute(name: string, handler: (route: any, from: any) => any) {
            observer.onRouteChange((to: any, from: any) => {
                if (to && to.name === name) {
                    handler(to, from);
                }
            });
        },
        removeListeners() {
            // Watchers/subscriptions live for the app's lifetime.
        },
    };
    return observer;
}
